// ** Modules
import { useRouter } from 'next/router';

// ** UI
import Chip from '@mui/material/Chip';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import Box, { type BoxProps } from '@mui/material/Box';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';

// ** Components
import Translations from '@/components/common/Translations';
import { Icon } from '@/components/common/icons';

// ** Theme
import themeConfig from '@/theme/themeConfig';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Utils
import { hexToRGBA } from '@/utils/ui/styleUtils';
import { handleURLQueries, buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { Settings } from '@/types/contextTypes';
import type { NavLink, NavGroup } from '@/types/layoutProps';

type VerticalNavLinkProps = {
	item: NavLink;
	parent?: boolean;
	nestedlevel?: number;
	navHover?: boolean;
	settings: Settings;
	navVisible?: boolean;
	collapsedNavWidth: number;
	navigationBorderWidth: number;
	toggleNavVisibility: () => void;
	isSubToSub?: NavGroup | undefined;
};

// ** Styled Components
const MenuNavLink = styled(ListItemButton)<
	ListItemButtonProps & {
		active: string;
		href: string;
		target?: '_blank' | undefined;
		nestedlevel: number;
	}
>(({ theme, nestedlevel, active }) => {
	return {
		width: '100%',
		fontWeight: nestedlevel !== 2 ? 'bold' : undefined,
		color: nestedlevel ? theme.palette.primary.main : theme.palette.text.primary,
		padding: theme.spacing(2.25, 3.5),
		transition: 'opacity .25s ease-in-out, background .25s ease-in-out, color .25s ease-in-out',
		'&.active': {
			boxShadow: theme.shadows[3],
			// backgroundImage: `linear-gradient(98deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main} 94%)`
			background:
				nestedlevel === 1
					? hexToRGBA(theme.palette.primary.main, 0.6)
					: nestedlevel === 2
						? hexToRGBA(theme.palette.primary.main, 0.25)
						: theme.palette.primary.main,
			'& .MuiListItemIcon-root': {
				color:
					nestedlevel === 1
						? theme.palette.common.white
						: nestedlevel === 2
							? theme.palette.primary.main
							: theme.palette.common.white
			}
		},
		'&.active:hover': {
			background: nestedlevel === 2 ? hexToRGBA(theme.palette.primary.main, 0.3) : theme.palette.primary.lighter
		},
		'&.active .MuiTypography-root, &.active .MuiSvgIcon-root': {
			color: `${nestedlevel === 1 || (!nestedlevel && active) ? theme.palette.common.white : theme.palette.primary.main}`
		},
		'&:hover': {
			background: nestedlevel === 2 ? hexToRGBA(theme.palette.primary.main, 0.25) : 'transparent'
		}
	};
});

const MenuItemTextMetaWrapper = styled(Box)<BoxProps>({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	transition: 'opacity .25s ease-in-out',
	overflow: 'hidden'
});

const VerticalNavLink = ({
	item,
	parent,
	navHover,
	settings,
	navVisible,
	nestedlevel = 0,
	collapsedNavWidth,
	toggleNavVisibility,
	navigationBorderWidth
}: VerticalNavLinkProps) => {
	// Hooks
	const router = useRouter();

	// Vars
	const { navcollapsed } = settings;
	const icon = parent && !item?.icon?.icon ? themeConfig.navSubItemIcon : item?.icon?.icon;
	const externalHref = item.url;
	const href = buildPathFromRelationLink(item.pageLink);
	const resolvedPath = href !== '/' ? href : externalHref || '/';

	const isNavLinkActive = (): boolean => router.asPath === resolvedPath || handleURLQueries(router, resolvedPath);

	return (
		<ListItem disablePadding sx={{ mt: nestedlevel ? 1.5 : 5, px: '0 !important' }}>
			<MenuNavLink
				active={isNavLinkActive().toString()}
				nestedlevel={nestedlevel}
				disabled={item.disabled || false}
				{...(item.disabled && { tabIndex: -1 })}
				className={isNavLinkActive() ? 'active' : ''}
				href={resolvedPath ?? '/'}
				{...(item.openInNewTab ? { target: '_blank' } : null)}
				onClick={(e) => {
					if (resolvedPath === undefined) {
						e.preventDefault();
						e.stopPropagation();
					}
					if (navVisible) {
						toggleNavVisibility();
					}
				}}
				sx={{
					py: nestedlevel ? 1 : 2.25,
					...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
					pl: navcollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 24) / 8 : 5.5,
					pr: navcollapsed && !navHover ? ((collapsedNavWidth - navigationBorderWidth - 24) / 2 - 5) / 4 : 3.5
				}}
			>
				{nestedlevel ? null : (
					<ListItemIcon
						sx={{
							minWidth: 'unset',
							color: 'primary.main',
							transition: 'margin .25s ease-in-out',
							...(parent && navcollapsed && !navHover ? {} : { mr: 2.5 }),
							...(navcollapsed && !navHover ? { mr: 0 } : {}), // this condition should come after (parent && navcollapsed && !navHover) condition for proper styling
							...(parent && item.children && item.children?.length > 0 ? { ml: 1.25, mr: 3.75 } : {})
						}}
					>
						<Icon name={icon as string} />
					</ListItemIcon>
				)}

				<MenuItemTextMetaWrapper sx={{ opacity: navcollapsed && !navHover ? 0 : 1 }}>
					<Typography
						{...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navcollapsed && !navHover)) && {
							noWrap: true
						})}
						sx={{
							fontFamily: nestedlevel === 2 ? DIN : undefined
						}}
					>
						<Translations text={item.title} />
					</Typography>

					{item.badgeContent && (
						<Chip
							label={item.badgeContent}
							color={item.badgeColor || 'primary'}
							sx={{
								height: 20,
								fontWeight: 500,
								'& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' }
							}}
						/>
					)}
				</MenuItemTextMetaWrapper>
			</MenuNavLink>
		</ListItem>
	);
};

export default VerticalNavLink;
